import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { WEB_COMPONENT_GZ_RM_CVES_SEVERITY_WIDGET } from '../../../../webcomponents.constants';
import { RmCvesBySeverityPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { InventoryFilter, NavigationHelper } from '@nirvana/shared/navigation';
import { AnswerType, ColumnKeys as VulnerabilitiesColumnKeys } from '../../../vulnerabilitiesApp/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget, ROW_HEIGHT_PX } from '../../resize';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderLearnMoreAboutCves, renderNISTLink, renderText } from '../../text';
import { translateFn } from '../../../../utils/translator.helper';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { FontSizes } from '../../../../types/common/types';
import { WidgetHeaderItemSize } from '../../../../ui/gzWidgetHeaderItem/types';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-cves-severity-widget
 */
@customElement(WEB_COMPONENT_GZ_RM_CVES_SEVERITY_WIDGET)
export class GzRmCvesSeverityWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmCvesBySeverityPayload = { topCvesBySeverity: { companyId: '', totalNonZeroScore: 0, items: [] } };

  static get styles() {
    return styles;
  }

  protected viewAll(activelyExploited?: boolean): void {
    const filters: InventoryFilter<VulnerabilitiesColumnKeys>[] = [
      {
        [VulnerabilitiesColumnKeys.STATE]: { in: [State.ACTIVE] },
      },
      {
        [VulnerabilitiesColumnKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
      },
    ];

    if (activelyExploited) {
      filters.push({ [VulnerabilitiesColumnKeys.TARGETS_YOUR_INDUSTRY]: { in: [AnswerType.YES] } });
    }

    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topCvesBySeverity?.companyId ?? '' },
      filters,
      section: 'vulnerabilities',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    const items: Row[] =
      this.payload.topCvesBySeverity?.items?.map(
        ({ cve, score }): RowConfig => ({
          data: { cve },
          cells: [
            renderText(cve),
            {
              disableClick: true,
              html: renderNISTLink(cve),
              data: null,
            },
            html` <gz-rm-severity .score="${score}" reverse></gz-rm-severity>`,
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), renderText('...', true), html` <gz-rm-severity noHighlight reverse></gz-rm-severity>`];
    const fullItems = fitItemsToWidget(
      fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem),
      this.contentHeight - 4 * ROW_HEIGHT_PX
    );

    return html`
      ${this.renderHeader(
        this.payload.topCvesBySeverity?.totalCvesCount ?? 0,
        this.payload.topCvesBySeverity?.activelyExploitedCount ?? 0
      )}

      <gz-basic-table
        class="mb-5"
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { cve: string }) => {
          if (!rowData) {
            return;
          }

          const { cve } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topCvesBySeverity?.companyId ?? '' },
            section: 'vulnerabilities',
            filters: [
              {
                [VulnerabilitiesColumnKeys.CVES]: {
                  complexEnumFilter: [
                    {
                      label: cve.toUpperCase(),
                      value: cve.toUpperCase(),
                    },
                  ],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      ${renderLearnMoreAboutCves()}

      <gz-link
        class="d-block"
        type="${LinkType.QUIET}"
        fontSize="${FontSizes.PARAGRAPH_11}"
        .callback=${() => this.viewAll()}
      >
        ${translateFn('View all')} (${this.payload.topCvesBySeverity?.totalNonZeroScore ?? 0})
      </gz-link>
    `;
  }

  private renderHeader(totalCves: number, activelyExploited: number): TemplateResult {
    return html`
      <div class="header mt-4 mb-4">
        ${this.renderHeaderItem(translateFn('total vulnerabilities'), totalCves, undefined, () => this.viewAll())}
        <div class="vertical-bar"></div>
        ${this.renderHeaderItem(
          translateFn('actively exploited'),
          activelyExploited,
          'ds-icon-notification_negative',
          () => this.viewAll(true)
        )}
      </div>
    `;
  }

  private renderHeaderItem(name: string, value?: number, iconClas?: string, onClick?: Function): TemplateResult {
    return html`
      <gz-widget-header-item
        .name="${name}"
        .value="${value ?? 0}"
        .size="${WidgetHeaderItemSize.BIG}"
        .state="${!value ? WidgetStateFrontEnd.IS_EMPTY : this.state}"
        .iconClass="${iconClas}"
        .clickable="${!!value}"
        @click="${value && onClick ? () => onClick() : undefined}"
      ></gz-widget-header-item>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-cves-severity-widget': GzRmCvesSeverityWidgetWebComponent;
  }
}
